import React, { useEffect, useRef, useState, useCallback } from 'react';

const COLORS = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#10b981',
  yellow: '#f59e0b',
};

const COLOR_KEYS = { red: 'w', blue: 'a', green: 's', yellow: 'd' };

/**
 * ReactionTest — Module 1
 * Three phases: simple RT, choice RT, go/no-go
 * All timing via performance.now() + requestAnimationFrame
 */
export default function ReactionTest({ trials, onComplete, icon, moduleNum = 1, totalModules = 6 }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    phase: 'simple',
    trialIndex: 0,
    onsetTime: null,
    awaitingResponse: false,
    results: [],
    timeoutId: null,
  });
  const [uiState, setUiState] = useState('instructions'); // instructions | running | done
  const [instructionPhase, setInstructionPhase] = useState('simple');
  const [progress, setProgress] = useState(0);

  // Warmup state
  const warmupPhase = useRef(true);
  const [warmupComplete, setWarmupComplete] = useState(false);
  const [warmupDone, setWarmupDone] = useState(false); // persists after overlay clears
  const warmupIndexRef = useRef(0);

  const WARMUP_SIMPLE = [
    { type: 'simple', isi: 1000 },
    { type: 'simple', isi: 1300 },
    { type: 'simple', isi: 900 },
  ];

  useEffect(() => {
    document.body.style.cursor = uiState === 'running' ? 'none' : '';
    return () => { document.body.style.cursor = ''; };
  }, [uiState]);

  const totalTrials = trials.length;

  // Separate trials by type
  const simpleTrials = trials.filter((t) => t.type === 'simple');
  const choiceTrials = trials.filter((t) => t.type === 'choice');
  const gonogoTrials = trials.filter((t) => t.type === 'gonogo');

  const trialsByPhase = useRef({ simple: simpleTrials, choice: choiceTrials, gonogo: gonogoTrials });
  const phaseIndexRef = useRef({ simple: 0, choice: 0, gonogo: 0 });

  const drawBackground = useCallback((ctx, canvas) => {
    ctx.fillStyle = '#0a0e1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const drawFixation = useCallback((ctx, canvas) => {
    drawBackground(ctx, canvas);
    ctx.fillStyle = '#4b5a72';
    ctx.font = 'bold 36px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('+', canvas.width / 2, canvas.height / 2);
  }, [drawBackground]);

  // --- Warmup runner ---

  const runWarmupTrialRef = useRef(null);
  runWarmupTrialRef.current = () => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    if (warmupIndexRef.current >= WARMUP_SIMPLE.length) {
      // All warmup trials done
      s.awaitingResponse = false;
      clearTimeout(s.timeoutId);
      setWarmupComplete(true);
      setTimeout(() => {
        warmupPhase.current = false;
        warmupIndexRef.current = 0;
        setWarmupComplete(false);
        setWarmupDone(true);
        // Reset real test state
        s.phase = 'simple';
        s.trialIndex = 0;
        s.results = [];
        s.awaitingResponse = false;
        phaseIndexRef.current = { simple: 0, choice: 0, gonogo: 0 };
        setInstructionPhase('simple');
        setUiState('instructions');
      }, 2000);
      return;
    }

    const trial = WARMUP_SIMPLE[warmupIndexRef.current];
    s.awaitingResponse = false;
    s.onsetTime = null;

    drawFixation(ctx, canvas);

    s.timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        drawBackground(ctx, canvas);
        // Draw white circle (simple RT)
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, 40, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        s.onsetTime = performance.now();
        s.awaitingResponse = true;

        // Auto-advance on no response
        s.timeoutId = setTimeout(() => {
          if (s.awaitingResponse) {
            s.awaitingResponse = false;
            warmupIndexRef.current++;
            setTimeout(() => runWarmupTrialRef.current?.(), 300);
          }
        }, 1500);
      });
    }, trial.isi || 1000);
  };

  const runNextTrial = useCallback(() => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const phaseTrials = trialsByPhase.current[s.phase];
    const phaseIndex = phaseIndexRef.current[s.phase];

    if (phaseIndex >= phaseTrials.length) {
      // Advance to next phase
      const phaseOrder = ['simple', 'choice', 'gonogo'];
      const nextPhaseIdx = phaseOrder.indexOf(s.phase) + 1;
      if (nextPhaseIdx >= phaseOrder.length) {
        // All done
        setUiState('done');
        onComplete(s.results);
        return;
      }
      const nextPhase = phaseOrder[nextPhaseIdx];
      s.phase = nextPhase;
      setInstructionPhase(nextPhase);
      setUiState('instructions');
      return;
    }

    const trial = phaseTrials[phaseIndex];
    s.awaitingResponse = false;
    s.onsetTime = null;

    drawFixation(ctx, canvas);
    setProgress(Math.round((s.results.length / totalTrials) * 100));

    s.timeoutId = setTimeout(() => {
      // Show stimulus via rAF to sync with display refresh
      requestAnimationFrame(() => {
        drawBackground(ctx, canvas);

        if (trial.type === 'simple') {
          // White circle
          ctx.beginPath();
          ctx.arc(canvas.width / 2, canvas.height / 2, 40, 0, Math.PI * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fill();

        } else if (trial.type === 'choice') {
          // Colored circle + key hint
          ctx.beginPath();
          ctx.arc(canvas.width / 2, canvas.height / 2, 50, 0, Math.PI * 2);
          ctx.fillStyle = COLORS[trial.target];
          ctx.fill();
          ctx.font = '18px sans-serif';
          ctx.fillStyle = 'rgba(255,255,255,0.5)';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`[${COLOR_KEYS[trial.target].toUpperCase()}]`, canvas.width / 2, canvas.height / 2 + 80);

        } else if (trial.type === 'gonogo') {
          if (trial.isGo) {
            // Green circle — press SPACE
            ctx.beginPath();
            ctx.arc(canvas.width / 2, canvas.height / 2, 45, 0, Math.PI * 2);
            ctx.fillStyle = '#10b981';
            ctx.fill();
          } else {
            // Red X — do NOT press
            ctx.strokeStyle = '#ef4444';
            ctx.lineWidth = 8;
            ctx.lineCap = 'round';
            const cx = canvas.width / 2, cy = canvas.height / 2, r = 40;
            ctx.beginPath(); ctx.moveTo(cx - r, cy - r); ctx.lineTo(cx + r, cy + r); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx + r, cy - r); ctx.lineTo(cx - r, cy + r); ctx.stroke();
          }
        }

        s.onsetTime = performance.now();
        s.awaitingResponse = true;

        // Auto-advance on no response (miss)
        s.timeoutId = setTimeout(() => {
          if (s.awaitingResponse) {
            recordResponse(trial, null, true); // no response = miss
          }
        }, 1500);
      });
    }, trial.isi || 1000);
  }, [drawBackground, drawFixation, onComplete, totalTrials]); // eslint-disable-line

  const recordResponse = useCallback((trial, rt, miss = false) => {
    const s = stateRef.current;
    s.awaitingResponse = false;
    clearTimeout(s.timeoutId);

    let correct = false;
    if (miss) {
      correct = trial.type === 'gonogo' && !trial.isGo; // Missing a no-go is correct
    } else {
      if (trial.type === 'simple') correct = true;
      else if (trial.type === 'choice') correct = true; // Key matching handled in handler
      else if (trial.type === 'gonogo') correct = trial.isGo; // Pressing on go = correct
    }

    s.results.push({
      type: trial.type,
      rt: miss ? null : rt,
      correct,
      isGo: trial.isGo,
      target: trial.target,
    });

    phaseIndexRef.current[s.phase]++;
    setTimeout(runNextTrial, 300);
  }, [runNextTrial]);

  // Key handler — handles both warmup and real test
  useEffect(() => {
    if (uiState !== 'running') return;

    const handleKey = (e) => {
      const s = stateRef.current;
      if (!s.awaitingResponse || s.onsetTime === null) return;

      if (warmupPhase.current) {
        // Warmup: only Space, discard result
        if (e.code === 'Space') {
          e.preventDefault();
          s.awaitingResponse = false;
          clearTimeout(s.timeoutId);
          warmupIndexRef.current++;
          setTimeout(() => runWarmupTrialRef.current?.(), 300);
        }
        return;
      }

      const rt = performance.now() - s.onsetTime;
      const phaseTrials = trialsByPhase.current[s.phase];
      const trial = phaseTrials[phaseIndexRef.current[s.phase]];

      if (s.phase === 'simple' && e.code === 'Space') {
        e.preventDefault();
        recordResponse(trial, rt);
      } else if (s.phase === 'choice') {
        const pressedKey = e.key.toLowerCase();
        const expectedKey = COLOR_KEYS[trial.target];
        if (pressedKey === expectedKey) {
          recordResponse(trial, rt);
        }
      } else if (s.phase === 'gonogo' && e.code === 'Space') {
        e.preventDefault();
        recordResponse(trial, rt);
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [uiState, recordResponse]);

  const startPhase = () => {
    setUiState('running');
    setTimeout(runNextTrial, 500);
  };

  const startWarmup = () => {
    warmupPhase.current = true;
    warmupIndexRef.current = 0;
    stateRef.current.awaitingResponse = false;
    clearTimeout(stateRef.current.timeoutId);
    setUiState('running');
    setTimeout(() => runWarmupTrialRef.current?.(), 500);
  };

  const phaseInstructions = {
    simple: {
      title: 'Simple Reaction',
      text: 'A white circle will appear. Press SPACE as fast as you can.',
      key: 'SPACE',
    },
    choice: {
      title: 'Choice Reaction',
      text: 'A colored circle will appear. Press the matching key: W=Red, A=Blue, S=Green, D=Yellow.',
      key: 'W / A / S / D',
    },
    gonogo: {
      title: 'Go / No-Go',
      text: 'Press SPACE on GREEN circles. Do NOT press on RED X marks.',
      key: 'SPACE (green only)',
    },
  };

  const inst = phaseInstructions[instructionPhase];

  return (
    <div style={{ width: '100%' }}>
      {uiState === 'instructions' && (
        <div className="card text-center" style={{ maxWidth: 480, margin: '0 auto' }}>
          {icon && <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-dim)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', margin: '0 auto 20px' }}>{icon}</div>}
          <h2 style={{ marginBottom: 8 }}>{inst.title}</h2>
          {warmupDone && instructionPhase === 'simple' && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(16,185,129,0.12)', border: '1px solid #10b981',
              borderRadius: 8, padding: '4px 12px', marginBottom: 12,
              fontSize: 13, color: '#10b981', fontWeight: 600,
            }}>
              ✓ Warmup complete
            </div>
          )}
          <p style={{ marginBottom: 8 }}>{inst.text}</p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
            Key: <span className="text-accent mono">{inst.key}</span>
          </p>
          {/* First phase: show warmup button (before warmup) or start real test button (after warmup) */}
          {instructionPhase === 'simple' && !warmupDone ? (
            <button className="btn btn--primary" onClick={startWarmup}>
              Start
            </button>
          ) : (
            <button className="btn btn--primary" onClick={startPhase}>
              {warmupDone && instructionPhase === 'simple' ? 'Start Real Test' : 'Start'}
            </button>
          )}
        </div>
      )}

      {uiState === 'running' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="progress-bar" style={{ flex: 1 }}>
              <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
            </div>
            <span style={{ fontSize: 13, whiteSpace: 'nowrap', color: warmupPhase.current && !warmupComplete ? '#f59e0b' : 'var(--text-muted)' }}>
              {warmupPhase.current && !warmupComplete ? 'PRACTICE — not scored' : `${progress}%`}
            </span>
          </div>
          <div style={{ position: 'relative' }}>
            <div className="canvas-wrapper">
              <canvas
                ref={canvasRef}
                width={600}
                height={400}
                style={{ width: '100%', maxWidth: 600, background: '#0a0e1a' }}
              />
            </div>
            {warmupComplete && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(10,14,26,0.92)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                borderRadius: 8,
              }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>
                  Warmup Complete!
                </div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
                  Returning to instructions...
                </div>
              </div>
            )}
          </div>
          <p className="text-center text-muted" style={{ fontSize: 13 }}>
            Module {moduleNum} of {totalModules} — Reaction & Precision
          </p>
        </div>
      )}
    </div>
  );
}
